import { Component, computed, inject, signal } from '@angular/core';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { Currency } from '../../../core/services/currency';
import { Theme } from '../../../core/services/theme';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { uiConfig } from '@brand/config/ui.config';
import { filter } from 'rxjs';
import { LOCAL_ROUTES } from '../../../core/constants/local-routes.constants';
import { CurrencyCode } from '../../../core/data/currencies';
import { Platform } from '../../../core/services/platform';
import { UserState } from '../../../features/account/services/user.state';
import { AuthState } from '../../../features/auth/services/auth.state';
import { CartState } from '../../../features/cart/services/cart.state';
import { Avatar } from '../../../shared/components/avatar/avatar';
import { IconButton } from '../../../shared/components/buttons/icon-button/icon-button';
import { PrimaryAnimatedButton } from '../../../shared/components/buttons/primary-animated-button/primary-animated-button';
import {
  HeaderAuthButtonVariant,
  PrimaryButton,
  PrimaryButtonVariant,
} from '../../../shared/components/buttons/primary-button/primary-button';
import { DropdownFormControl } from '../../../shared/components/form-controls/dropdown-form-control/dropdown-form-control';
import { DollarCircleBold } from '../../../shared/components/icons/dollar-circle-bold';
import { MenuOutline } from '../../../shared/components/icons/menu-outline';
import { MoonOutline } from '../../../shared/components/icons/moon-outline';
import { ShoppingCartOutline } from '../../../shared/components/icons/shopping-cart-outline';
import { SunOutline } from '../../../shared/components/icons/sun-outline';
import { CURRENCY_OPTIONS } from '../../../shared/helpers/currency-options';
import { NavItem } from '../nav-item/nav-item';
import { SidebarMobile } from '../sidebar-mobile/sidebar-mobile';

@Component({
  selector: 'ui-header',
  imports: [
    IconButton,
    PrimaryButton,
    PrimaryAnimatedButton,
    DropdownFormControl,
    NgClickOutsideDirective,
    NavItem,
    Avatar,
    SidebarMobile,
    RouterLink,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class Header {
  private currency = inject(Currency);
  private theme = inject(Theme);
  private platform = inject(Platform);

  public authState = inject(AuthState);
  public userState = inject(UserState);
  public cartState = inject(CartState);

  menuMobileOpen = signal(false);
  sidebarMobileOpen = signal(false);

  routes = LOCAL_ROUTES;
  seoAuthor = uiConfig.brand.name;
  loginButton = uiConfig.header.loginButton;
  registerButton = uiConfig.header.registerButton;

  userName = computed<string>(() => {
    const user = this.userState.user();
    if (!user) return '';
    return `${user.usuario_nombres} ${user.usuario_apellidos}`;
  });
  userImage = computed<string>(() => {
    const user = this.userState.user();
    if (!user) return '';
    return `${user.usuario_imagen}`;
  });
  userEmail = computed<string>(() => {
    const user = this.userState.user();
    if (!user) return '';
    return `${user.usuario_correo}`;
  });

  iconTheme = computed(() => {
    return this.darkMode() ? SunOutline : MoonOutline;
  });

  MenuOutline = MenuOutline;
  ShoppingCartOutline = ShoppingCartOutline;
  DollarCircleBold = DollarCircleBold;
  currencyOptions = CURRENCY_OPTIONS;
  currentCode = this.currency.currentCode;
  darkMode = this.theme.darkMode;

  constructor() {
    inject(Router)
      .events.pipe(
        filter(e => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(() => {
        if (this.platform.isServer) return;
        this.menuMobileOpen.set(false);
        this.sidebarMobileOpen.set(false);
      });
  }

  asPrimaryVariant(variant: HeaderAuthButtonVariant): PrimaryButtonVariant {
    return variant === 'animated' ? 'primary' : variant;
  }

  setCurrency(value: CurrencyCode | null): void {
    if (!value) return;
    this.currency.setCurrency(value);
  }

  setDarkMode(value: boolean): void {
    this.theme.setDarkMode(value);
  }

  toggleMenuMobile(): void {
    this.sidebarMobileOpen.set(false);
    this.menuMobileOpen.set(!this.menuMobileOpen());
  }

  toggleSidebarMobile(): void {
    this.menuMobileOpen.set(false);
    this.sidebarMobileOpen.set(!this.sidebarMobileOpen());
  }
}
